<template>
    <div class="row">
        <div v-for="(product, index) of products" v-bind:key="index" class="col-12 col-md-6 col-xl-4">
            <div class="card mb-4">
                <div class="card-body d-flex justify-content-between">
                    <div>{{product.name}} x{{product.quantity}}</div>
                    <div class="close-button" @click="remove(index)">
                        <i class="bi-x"></i>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <div>
        <button type="button" class="btn btn-secondary" :class="{ 'is-invalid': v && v.$error }" v-on:click="addProduct">
            Add Product
        </button>
        <span class="invalid-feedback" v-if="v && v.$error">{{v.$errors[0].$message}}</span>
    </div>
    <div class="modal fade" id="product-modal" tabindex="-1">
        <div class="modal-dialog modal-dialog-scrollable product-modal-dialog">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">Add product</h5>
                    <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                </div>
                <div v-if="!productSelection" class="product-search px-3 pt-3 pb-3">
                    <input
                        type="text"
                        class="form-control"
                        placeholder="Search products..."
                        v-model="searchTerm"
                    />
                </div>
                <div class="modal-body">
                    <nav v-if="!isSearching && productCategorySelections.length">
                        <ol class="breadcrumb">
                            <li class="breadcrumb-item" v-for="(productCategorySelection) of productCategorySelections" v-bind:key="productCategorySelection.id">{{productCategorySelection.name}}</li>
                        </ol>
                    </nav>
                    <div v-if="!isSearching && showFlash">
                      <Flash alert="warning">There are no products available in this category for this combination of service level and impression type.</Flash>
                    </div>
                    <div v-if="isSearching && !productSelection && !searchResults.length">
                      <Flash alert="warning">No products match your search.</Flash>
                    </div>
                    <div v-if="!productSelection" class="d-grid gap-2">
                        <template v-if="!isSearching">
                            <button type="button" class="btn btn-secondary" v-for="productCategory of productCategories" v-bind:key="productCategory.id" v-on:click.prevent="selectCategory(productCategory)">{{productCategory.name}}</button>
                        </template>
                        <div class="product-card p-3 mb-2 rounded border bg-light d-flex justify-content-between align-items-center" v-for="productChoice of displayedProductChoices" v-bind:key="productChoice.id">
                            <div class="flex-grow-1">
                                <div class="mb-0 d-flex justify-content-between align-items-center">
                                    <div>
                                        <div class="d-flex align-items-center">
                                            <span>{{ productChoice.name }}</span>
                                        </div>
                                        <div class="d-flex gap-4" style="font-size: 1rem;">
                                            <span v-if="productChoice.price" class="text-muted"> 
                                                £{{ Number(productChoice.price).toFixed(2) }}
                                            </span>
                                            <div v-if="productChoice.turnaround_time" class="text-muted d-flex align-items-center gap-1"> 
                                                <i class="bi bi-clock" style="font-size: 0.8em;"></i>
                                                {{ productChoice.turnaround_time }} Days
                                            </div>
                                        </div>
                                    </div>
                                    <div>
                                        <button type="button" class="btn btn-primary" @click.prevent="selectProduct(productChoice)">Select</button>
                                    </div>
                                </div>
                                <div v-if="productChoice.short_description" class="text-muted mb-0 mt-2" style="font-size: 0.9rem;">
                                    {{ productChoice.short_description }}
                                </div>
                                <div v-if="pricing_tier == this.pricingTierChoice && (pricing_tier != productChoice.pricing_tier)" class="badge choice-badge" style="font-size: 0.9rem;">
                                    Not eligible for Swift Choice
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-if="productSelection">
                        <div class="mb-2">
                            <strong>{{productSelection.name}}<span v-if="productSelection.is_aligner == true"> x 1</span></strong>
                        </div>
                        <div class="mb-2">
                            <TextInput
                                label="Quantity"
                                v-model="quantity"
                                :v="v$.quantity"
                                isPreventSubmit="true"
                                v-if="productSelection.is_aligner == false"
                            />
                        </div>
                        <div class="mb-2" v-if="productSelection.materials.length">
                            <ButtonInput
                                label="Material"
                                v-model="material"
                                :v="v$.material"
                                :options="productSelection.materials"
                            />
                        </div>
                    </div>
                </div>
                <div class="modal-footer" v-if="productCategorySelections.length || productSelection">
                    <button v-if="!productSelection && productCategorySelections.length" type="button" class="btn btn-light mt-2" :class="{'disabled': !productCategorySelections.length}" v-on:click.prevent="backCategory">Back</button>
                    <button v-if="productSelection" type="button" class="btn btn-primary" @click="submit">Save changes</button>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
    .close-button
    {
        .bi
        {
            cursor: pointer;
        }
    }

    .product-card {
        transition: box-shadow 0.2s ease;
        &:hover {
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        }
    }

    .choice-badge {
        color: #f8f9fa;
        background-color: #8c8c8c !important;
    }

    @media (min-width: 768px) {
        .product-card button {
            width: auto !important;
        }
    }
</style>

<script>
    import api from "@/services/api";
    import TextInput from '@/components/TextInput.vue';
    import ButtonInput from '@/components/ButtonInput.vue';
    import useVuelidate from '@vuelidate/core'
    import { required, requiredIf, minValue, maxValue } from '@vuelidate/validators'
    import { Modal } from 'bootstrap';
    import Flash from "@/components/Flash.vue";

    const PRICING_TIER_CHOICE = 2;

    export default {
        props: {
            modelValue: {
                required: true,
            },
            v: {
                required: true,
            },
            service_level: {
                required: true,
            },
            displayPopup: {
                required: false,
                default: true,
            },
            accountId: {
                required: true,
            },
            order_id: {
                required: true,
            },
            impressionType: {
                required: false,
            },
            pricing_tier: {
                required: false,
            },
        },
        setup () {
            return {
                v$: useVuelidate()
            }
        },
        mounted() {
            if(!this.products.length && this.displayPopup && !this.isRemake)
            {
                this.addProduct();
            }
        },
        data() {
            return {
                productCategories: [],
                productChoices: [],
                productCategorySelections: [],
                productSelection: null,
                quantity: 1,
                material: null,
                products: this.modelValue,
                modal: null,
                showFlash: false,
                pricingTier: this.pricing_tier,
                pricingTierChoice: PRICING_TIER_CHOICE,
                searchTerm: '',
                searchableProducts: [],
            }
        },
        computed: {
            isSearching() {
                return this.searchTerm.trim().length > 0;
            },
            searchResults() {
                if (!this.isSearching) {
                    return [];
                }
                var needle = this.searchTerm.trim().toLowerCase();
                return this.searchableProducts.filter((product) => {
                    return product.name && product.name.toLowerCase().includes(needle);
                });
            },
            displayedProductChoices() {
                return this.isSearching ? this.searchResults : this.productChoices;
            },
        },
        validations () {
            return {
                quantity: { required, minValue: minValue(1), maxValue: maxValue(99) },
                material: { requiredIf: requiredIf(this.productSelection && this.productSelection.materials.length) },
            }
        },
        components: {
          Flash,
            TextInput,
            ButtonInput,
        },
        emits: ['update:modelValue'],
        methods: {
            fetchCategories: function(category) {       
                var categoryId = category === null ? null : category.id;

                this.productCategories = [];
                this.productChoices = [];

                api
                .get('/api/v1/products/categories', {
                    params: {
                        category_id: categoryId,
                        practice_id: this.$store.state.currentPracticeId,
                        account_id: this.accountId,
                        order_id: this.order_id,
                        impression_type: this.impressionType,
                        service_level: this.service_level,
                        pricing_tier: this.pricing_tier
                    },
                })
                .then((response) => {
                    if(response.data.categories && response.data.categories.length)
                    {
                        this.productCategories = response.data.categories;
                        this.showFlash = false;
                    }
                    else if(response.data.products && response.data.products.length)
                    {
                        this.productChoices = response.data.products;
                        this.showFlash = false;
                    } else {
                      this.showFlash = true;
                    }
                })
                .catch(() => {
                    console.log('Catch categories error');
                });        
            },
            resetForm: function()
            {
                this.v$.$reset();

                this.productCategories = [];
                this.productChoices = [];
                this.productCategorySelections = [];
                this.productSelection = null;
                this.quantity = 1;
                this.material = null;
                this.searchTerm = '';
            },
            fetchSearchableProducts: function()
            {
                api
                .get('/api/v1/productsList', {
                    params: {
                        practice_id: this.$store.state.currentPracticeId,
                        account_id: this.accountId,
                        service_level: this.service_level,
                        pricing_tier: this.pricing_tier,
                    },
                })
                .then((response) => {
                    this.searchableProducts = response.data.products;
                })
                .catch(() => {
                    console.log('Catch searchable products error');
                });
            },
            selectCategory: function(category)
            {
                this.productCategorySelections.push(category);
                this.fetchCategories(category);
            },
            backCategory: function()
            {
                this.productCategorySelections.pop();
                var category = this.productCategorySelections.at(-1);
                category = typeof category === 'undefined' ? null : category;
                this.fetchCategories(category);
            },
            addProduct: function()
            {
                this.modal = new Modal(document.getElementById('product-modal'));
                this.modal.show();

                this.resetForm();
                this.fetchCategories(null);
                this.fetchSearchableProducts();
            },
            selectProduct: function(product)
            {
                this.productCategories = [];
                this.productChoices = [];

                api
                .get('/api/v1/products/' + product.id, {
                    params: {
                        practice_id: this.$store.state.currentPracticeId,
                    }
                })
                .then((response) => {
                    product.materials = response.data.materials;
                    this.productSelection = product;
                    switch(parseInt(this.service_level))
                    {
                        case 1: this.material = response.data.default_materials.nhs; break;
                        case 2: this.material = response.data.default_materials.ind; break;
                        case 3: this.material = response.data.default_materials.prv; break;
                        default: this.material = null; break;
                    }
                })
                .catch(() => {
                    console.log('Catch product error');
                });   
            },
            submit: async function () {
                var isValid = await this.v$.$validate();

                if(isValid)
                {
                    this.products.push({
                        id: this.productSelection.id,
                        name: this.productSelection.name,
                        quantity: this.quantity,
                        material_id: this.material,
                        requires_shade: this.productSelection.requires_shade,
                        is_aligner: this.productSelection.is_aligner,
                    });

                    this.$emit('update:modelValue', this.products);

                    this.modal.hide();
                }
                else
                {
                    console.log('Validation errors', this.v$.$errors.map((error) => { return error.$property + ': ' + error.$message}));
                }
            },
            remove: function(index)
            {
                this.products.splice(index, 1);
                this.$emit('update:modelValue', this.products);
            },
        },
    }
</script>
